// Wellness Insights

import React, { useEffect, useState } from "react";
import "./Therapy.css";
import Loading from "../../components/Utilities/Loading/Loading";
import { GrDownload } from "react-icons/gr";
import { CiCircleInfo } from "react-icons/ci";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import Rating from "@material-ui/lab/Rating";
import { Progress } from "antd";
import { useUserTokenSelector } from "store/reducers";
import {
  getFeedbackInfoByCorporateId,
  getSessionInfo,
  getTeamLocationForFilter, // Import the action
} from "actions/assessment";
import { startLoading, stopLoading } from "store/actions";
import { useDispatch } from "react-redux";
import { count } from "firebase/firestore";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Template } from "Containers/Overview/template";

export const Therapy = ({ adminCorporateId }) => {
  const userToken = useUserTokenSelector();
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [selectedYear, setSelectedYear] = useState("All Time");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectTimeRange, setSelectTimeRange] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("All");
  const [selectedTeam, setSelectedTeam] = useState("All Teams"); // Default to "All Teams"
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [customDateRange, setCustomDateRange] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const monthNames = [
    // This seems to be for a different filter, ensure it's still needed
    "All",
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const dispatch = useDispatch();
  const [locationArray, setLocationArray] = useState(["All Locations"]); // State for dynamic locations
  const [teamArray, setTeamArray] = useState(["All Teams"]); // State for dynamic teams
  const [groupedTeamLocations, setGroupedTeamLocations] = useState({}); // To store team-location mapping
  const [timeRangeArray, setTimeRangeArray] = useState([
    "Today",
    "This Week",
    "This Month",
    "Last 3 months",
    "Last 6 months",
    "Last Year",
    "This Year",
    "All Time",
    "Custom",
  ]);
  const [selectedRating, setSelectedRating] = useState(0);
  const [oneOnOneSessionData, setOneOnOneSessionData] = useState([]); // Already declared below
  const [repeatSessionData, setRepeatSessionData] = useState([]); // Already declared below
  const [totalRatingsState, setTotalRatingsState] = useState({}); // Renamed to avoid conflict
  const [averageRating, setAverageRating] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [yearsArray, setYearsArray] = useState(["All Time"]);
  const totalCount = Object.values(totalRatingsState).reduce(
    (a, b) => a + b,
    0
  );
  const [showReport, setShowReport] = useState(false);

  const [userInfo, setUserInfo] = useState({
    signups: {
      total: 0,
      previous: 0,
    },
    active: {
      total: 0,
      previous: 0,
    },
    logins: {
      total: 0,
      previous: 0,
    },
  });
  const [uniqueUsers, setUniqueUsers] = useState("");
  const [genderChartData, setGenderChartData] = useState([]);
  const [signedUpUsers, setSignedUpUsers] = useState("");
  const [monthlyLoginData, setMonthlyLoginData] = useState([]);
  const [currMonthSessionCount, setCurrMonthSessionCount] = useState("");
  const [sessionCountChange, setSessionCountChange] = useState("");
  const [uniqueUserChange, setUniqueUserChange] = useState("");

  const timeRangeMap = new Map([
    ["Today", "today"],
    ["This Week", "7d"],
    ["This Month", "30d"],
    ["Last 3 months", "3m"],
    ["Last 6 months", "6m"],
    ["Last Year", "lastYear"],
    ["This Year", "1y"],
    ["All Time", "all"],
    ["Custom", "custom"],
  ]);

  const handleTimeRangeChange = (e) => {
    const value = e.target.value;
    setSelectTimeRange(value);
    if (value === "custom") {
      setShowCustomCalendar(true);
    } else {
      setShowCustomCalendar(false);
    }
  };

  const handleCustomDateChange = (date) => {
    if (Array.isArray(date)) {
      const [startDate, endDate] = date;
      setCustomDateRange([startDate, endDate]);
      if (startDate && endDate) {
        setShowCustomCalendar(false);
      }
      return;
    }
    setCustomDateRange(date);
  };

  const getCustomDates = () => {
    if (Array.isArray(customDateRange)) {
      const [startDate, endDate] = customDateRange;
      return { startDate, endDate, singleDate: startDate };
    }
    return { startDate: null, endDate: null, singleDate: customDateRange };
  };

  function mapTimeRangeToApiValue(timeRange) {
    return timeRangeMap.get(timeRange) || "all";
  }

  useEffect(() => {
    const loadAllInitialData = async () => {
      try {
        await Promise.all([
          getFeedbackInfo(),
          getOneOnOneSessionInfo(),
          fetchTeamAndLocationFilters(),
        ]);
      } catch (error) {
        console.error("Error fetching initial therapy data:", error);
        // Optionally set an error state here
      } finally {
        setInitialLoadComplete(true);
      }
    };
    loadAllInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array means this runs once on component mount

  useEffect(() => {
    // Update location options when selectedTeam changes
    if (selectedTeam === "All Teams") {
      const allUniqueLocations = [
        "All Locations",
        ...new Set(Object.values(groupedTeamLocations).flat()),
      ].sort();
      setLocationArray(allUniqueLocations);
      setSelectedLocation("All Locations");
    } else if (groupedTeamLocations[selectedTeam]) {
      setLocationArray(
        ["All Locations", ...groupedTeamLocations[selectedTeam]].sort()
      );
      setSelectedLocation("All Locations"); // Reset location to "All" when team changes
    } else {
      setLocationArray(["All Locations"]);
      setSelectedLocation("All Locations");
    }
  }, [selectedTeam, groupedTeamLocations]);

  const getFeedbackInfo = async () => {
    // Local loading for this specific fetch, if needed, can be managed by setLoading(true/false)
    // The global loader is also dispatched here.
    dispatch(startLoading());

    let apiTimeRange = selectTimeRange;

    try {
      const res = await getFeedbackInfoByCorporateId(
        userToken,
        adminCorporateId,
        selectedTeam === "All Teams" ? "all" : selectedTeam,
        selectedLocation === "All Locations" ? "all" : selectedLocation,
        apiTimeRange,
        customDateRange
      );

      if (res && res.data) {
        const {
          ratingCounts,
          totalRatings: apiTotalRatings,
          userReviews,
        } = res.data;

        // Process ratingCounts
        const processedRatingCounts = {};
        let sumOfRatings = 0;
        for (const key in ratingCounts) {
          const value = Number(ratingCounts[key] || 0);
          if (!Number.isNaN(value)) {
            const numericRating = parseInt(key.split(" ")[0], 10);
            if (!Number.isNaN(numericRating)) {
              processedRatingCounts[numericRating] =
                (processedRatingCounts[numericRating] || 0) + value;
              sumOfRatings += numericRating * value;
            }
          }
        }
        setTotalRatingsState(processedRatingCounts);

        // Calculate averageRating
        if (apiTotalRatings > 0) {
          setAverageRating(sumOfRatings / apiTotalRatings);
        } else {
          setAverageRating(0);
        }

        // Process userReviews
        const processedReviews = userReviews.map((review) => ({
          name: review.userName,
          teamName: review.teamName,
          rating: review.rating,
          comment: review.review,
          comments: review.comments || [], // Add this line
        }));
        setReviews(processedReviews);
      }
    } catch (error) {
      console.error("Error fetching feedback info:", error);
      // Optionally, set error state here to display to the user
    } finally {
      dispatch(stopLoading());
    }
  };

  const fetchTeamAndLocationFilters = async () => {
    if (!userToken) return;
    dispatch(startLoading());
    try {
      const res = await getTeamLocationForFilter(userToken); // Assuming this action fetches the data
      if (res && res.data && Array.isArray(res.data.teamLocations)) {
        const teams = new Set();
        const locations = new Set();
        const groupedData = {};

        res.data.teamLocations.forEach((item) => {
          if (item.teamName) teams.add(item.teamName);
          if (item.location) locations.add(item.location);
          if (item.teamName) {
            if (!groupedData[item.teamName]) {
              groupedData[item.teamName] = [];
            }
            if (
              item.location &&
              !groupedData[item.teamName].includes(item.location)
            ) {
              groupedData[item.teamName].push(item.location);
            }
          }
        });
        setTeamArray(["All Teams", ...Array.from(teams).sort()]);
        // Initial location array will show all unique locations
        setLocationArray(["All Locations", ...Array.from(locations).sort()]);
        setGroupedTeamLocations(groupedData);
      }
    } catch (error) {
      console.error("Error fetching team/location filters for Therapy:", error);
    } finally {
      dispatch(stopLoading());
    }
  };
  useEffect(() => {
    const { startDate, endDate } = getCustomDates();
    const isCustomDateValid =
      selectTimeRange !== "custom" || (startDate && endDate);

    if (!isCustomDateValid) return;
    if (initialLoadComplete) {
      getOneOnOneSessionInfo();
      getFeedbackInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectTimeRange,
    selectedLocation,
    selectedTeam,
    adminCorporateId,
    userToken,
    initialLoadComplete,
    customDateRange
  ]);

  const getOneOnOneSessionInfo = async () => {
    // if (!userToken || !adminCorporateId) return;
    dispatch(startLoading());
    let apiTimeRange = selectTimeRange;
    try {
      // let apiTimeRange = selectTimeRange;

      const res = await getSessionInfo(
        userToken,
        adminCorporateId,
        selectedTeam === "All Teams" ? "all" : selectedTeam,
        selectedLocation === "All Locations" ? "all" : selectedLocation,
        apiTimeRange,
        customDateRange
      );

      if (res && res.data && res.data.status === "success" && res.data.data) {
        const { B2BSessionInfoData, yearsArray: apiYears } = res.data.data;
        setYearsArray(["All Time", ...apiYears.sort((a, b) => b - a)]);

        const dataForDisplay =
          B2BSessionInfoData[selectedYear] || // Use selectedYear first
          B2BSessionInfoData["bookingData"] || // Fallback to bookingData if specific time range
          B2BSessionInfoData["All Time"] || // Fallback to All Time
          {};

        if (B2BSessionInfoData["bookingData"]) {
          // Use monthlySessionData or periodData (fallback to empty array)
          setOneOnOneSessionData(
            (
              dataForDisplay.monthlySessionData ||
              dataForDisplay.periodData ||
              []
            ).map((d) => ({
              month: d.name,
              value: d.count,
            }))
          );
          const tt = (
            dataForDisplay.monthlySessionData ||
            dataForDisplay.periodData ||
            []
          ).map((d) => ({
            month: d.name,
            value: d.count,
          }));
        } else {
          // Create a year-wise summary map excluding "All Time"
          const yearWiseMap = Object.entries(B2BSessionInfoData)
            .filter(([year]) => year !== "All Time" && year !== "bookingData")
            .map(([year, data]) => ({
              month: year, // Consider renaming to 'year' for clarity
              value: data.totalSessions || 0,
            }));

          setOneOnOneSessionData(yearWiseMap);
        }

        setRepeatSessionData(
          (dataForDisplay.sessionCount || []).map((d) => ({
            month: d.name, // Ensure this matches XAxis dataKey="month"
            count: d.count, // Ensure this matches Bar dataKey="count"
          }))
        );
      } else {
        // Reset or set to default if data is not as expected
        console.warn("B2B Session Info data not in expected format:", res);
        // Reset states to default values if needed
      }
    } catch (error) {
      console.error("Error loading B2B session info:", error);
      // Handle error, maybe show a notification to the user
    } finally {
      dispatch(stopLoading());
    }
  };

  const wellnessData = () => {
    const dataJson = {
      signups: userInfo.signups,
      active: userInfo.active,
      logins: userInfo.logins,
      uniqueUsers: uniqueUsers,
      genderChartData: genderChartData,
      signedUpUsers: signedUpUsers,
      monthlyLoginData: monthlyLoginData,
      currMonthSessionCount: currMonthSessionCount,
      sessionCountChange: sessionCountChange,
      uniqueUserChange: uniqueUserChange,
      overviewItems: [],
      contentTrends: [],
      engagement: [],
      distribution: [],
      engagedEmp: [],
    };
    return dataJson;
  };

  const handleShowReport = () => {
    setIsDownloading(true);
    setShowReport(true);
    dispatch(startLoading());
  };

  const handleReportClose = () => {
    setShowReport(false);
    dispatch(stopLoading());
    setIsDownloading(false);
  };

  return (
    <div className="wellness-insights-container">
      {showCustomCalendar && (
        <div
          className="wellness-insights-overlay-background"
          onClick={() => setShowCustomCalendar(false)}
          aria-label="Close calendar"
        ></div>
      )}
      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="dashboardofboxes">
            <div className="wellness-insights-top">
              <div className="wellness-insights-filter">
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                >
                  {locationArray.map((location, index) => (
                    <option value={location}>{location}</option>
                  ))}
                </select>
              </div>
              <div className="wellness-insights-filter">
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                >
                  {teamArray.map((team, index) => (
                    <option value={team}>{team}</option>
                  ))}
                </select>
              </div>
              <div className="wellness-insights-filter">
                {/* <label htmlFor="timeRangeFilter">Time Range:</label> */}
                <select
                  id="timeRangeFilter"
                  value={selectTimeRange}
                  // onChange={(e) => setSelectTimeRange(e.target.value)}
                  onChange={handleTimeRangeChange}
                >
                  {timeRangeArray.map((time, index) => (
                    <option key={index} value={mapTimeRangeToApiValue(time)}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
              <div className={`wellness-insights-download-div ${isDownloading ? "disabled" : ""}`} onClick={!isDownloading ? handleShowReport : null}>
                <GrDownload />
                <span>Download</span>
              </div>
            </div>
            {showCustomCalendar && (
              <div className="custom-calendar-container">
            <Calendar
              onChange={handleCustomDateChange}
              value={customDateRange}
              selectRange
            />
          </div>
        )}
            <div className="wellness-insights-card">
              <div className="wellness-insights-card-heading">
                <span>1-on-1 Therapy Session Bookings</span>
                <CiCircleInfo />
              </div>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart
                  data={oneOnOneSessionData}
                  margin={{ top: 15, right: 15, bottom: 15, left: -25 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7bd3a1" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#7bd3a1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" tickLine={false} />
                  <YAxis tickLine={false}/>
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#2a9d8f"
                    fillOpacity={1}
                    fill="url(#colorValue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="wellness-insights-card">
              <div className="wellness-insights-card-heading">
                <span>Repeat Therapy Session Count</span>
                <CiCircleInfo />
              </div>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={repeatSessionData}
                  margin={{ top: 15, right: 15, bottom: 15, left: -25 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tickLine={false} />
                  <YAxis tickLine={false}/>
                  <Tooltip />
                  <Bar dataKey="count" fill="#52a17b" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="wellness-insights-card">
              <div className="wellness-insights-card-heading">
                <span>Ratings and Reviews</span> <CiCircleInfo />
              </div>

              <div className="wellness-insights-row">
                {/* Ratings Summary */}
                <div className="wellness-insights-ratings-summary">
                  <div className="wellness-insights-average-rating">
                    <p className="wellness-insights-card-subheading">
                      Average Rating
                    </p>
                    <div className="wellness-insights-stars-rating">
                      <p className="wellness-insights-ratingTxt">
                        {averageRating.toFixed(1)}
                      </p>
                      <Rating value={averageRating} precision={0.1} readOnly />
                    </div>
                  </div>

                  <div className="wellness-insights-rating-breakdown">
                    {[5, 4, 3, 2, 1].map((star) => (
                      <div key={star} className="wellness-insights-rating-row">
                        <span className="wellness-insights-star-label">
                          {star}
                          <span className="wellness-insights-star">★</span>
                        </span>
                        <Progress
                          percent={
                            ((totalRatingsState[star] || 0) /
                              (totalCount || 1)) *
                            100 // Use totalRatingsState
                          }
                          showInfo={false}
                          strokeColor="#4caf50"
                          trailColor="#e0e0e0"
                          className="rating-progress"
                        />
                        <span className="wellness-insights-count-label">
                          {(totalRatingsState[star] || 0) // Use totalRatingsState
                            .toString()
                            .padStart(2, "0")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="wellness-insights-seperator" />
                {/* Reviews */}
                <div className="wellness-insights-reviews-list">
                  <div
                    className="wellness-insights-row"
                    style={{ justifyContent: "space-between" }}
                  >
                    <p className="wellness-insights-card-subheading">Reviews</p>
                    <div className="wellness-insights-filter">
                      <select
                        className="review-filter-dropdown"
                        onChange={(e) =>
                          setSelectedRating(Number(e.target.value))
                        }
                        value={selectedRating}
                      >
                        <option value={0}>All</option>
                        {[5, 4, 3, 2, 1].map((star) => (
                          <option key={star} value={star}>
                            {star}
                            <p>★</p>
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {reviews
                    .filter(
                      (review) =>
                        selectedRating === 0 || review.rating === selectedRating
                    )
                    .map((review, index) => (
                      <div className="wellness-insights-review-card" key={index}>
                        {review.comments && review.comments.length > 0 && (
                          <div className="wellness-insights-review-comments">
                            {review.comments.map((comment, i) => (
                              <div key={i} className="wellness-insights-comment-bubble">
                                {comment}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="wellness-insights-review-header">
                          <div>
                            {/* <strong>{review.name}</strong> */}
                            {/* <div className="wellness-insights-review-team">
                              {review.teamName}
                            </div> */}
                          </div>
                          <Rating value={review.rating} readOnly />
                        </div>
                        <p className="wellness-insights-review-text">
                          {review.comment}
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showReport && <Template data={wellnessData()} close={handleReportClose} />}
    </div>
  );
};

export default Therapy;
